/**
 * Fabric.js v7 patch: let a canvas keep a backing store smaller than its
 * design size.
 *
 * Fabric sizes a canvas's elements at its design size, or larger with retina
 * scaling, but never smaller. Shown at a small zoom, a 1920 × 1920 ad then
 * fills many times the pixels on screen every frame. A canvas given a
 * `backingScale` uses it in place of the device ratio, below 1 as well, and
 * its elements and their drawing scale are sized to match. Object caches and
 * pointer mapping already follow the canvas's retina scaling.
 */
import { StaticCanvas } from 'fabric';
import type { BackingScaleHost } from '@/types/canvas/fabric';

const proto = StaticCanvas.prototype as unknown as BackingScaleHost;
const getRetinaScaling = proto.getRetinaScaling;
const setDimensionsImpl = proto._setDimensionsImpl;

proto.getRetinaScaling = function (this: BackingScaleHost) {
  if (this.enableRetinaScaling && this.backingScale !== undefined) {
    return this.backingScale;
  }
  return getRetinaScaling.call(this);
};

proto._setDimensionsImpl = function (
  this: BackingScaleHost,
  dimensions,
  options
) {
  setDimensionsImpl.call(this, dimensions, options);
  if (options?.cssOnly) return;

  const scale = this.getRetinaScaling();
  if (scale >= 1) return;
  for (const item of [this.elements.lower, this.elements.upper]) {
    if (!item) continue;
    item.el.width = Math.max(1, Math.round(this.width * scale));
    item.el.height = Math.max(1, Math.round(this.height * scale));
    item.ctx.scale(scale, scale);
  }
};
